import React, { useState, useEffect } from 'react';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import NavigationBreadcrumbs from '../../components/navigation/NavigationBreadcrumbs';
import BudgetCreationForm from './components/BudgetCreationForm';
import CategoryAllocationSliders from './components/CategoryAllocationSliders';
import SafeToSpendCalculator from './components/SafeToSpendCalculator';
import OverBudgetAlerts from './components/OverBudgetAlerts';
import MonthComparisonChart from './components/MonthComparisonChart';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { budgetsService } from '../../services/financialDataService';
import { supabase } from '../../lib/supabase';

const BudgetManagement = () => {
  const { user } = useAuth();
  const [activeBudget, setActiveBudget] = useState({
    name: 'Monthly Budget - February 2026',
    duration: 'monthly',
    totalAmount: 5000,
    startDate: '2026-02-01',
    endDate: '2026-02-28'
  });

  const [categories, setCategories] = useState([]);
  const [comparisonData] = useState([
    { month: 'Oct', budget: 5000, actual: 4650 },
    { month: 'Nov', budget: 5000, actual: 4890 },
    { month: 'Dec', budget: 5200, actual: 5150 },
    { month: 'Jan', budget: 5000, actual: 4720 },
    { month: 'Feb', budget: 5000, actual: 4740 }
  ]);
  const [loading, setLoading] = useState(true);

  // Fetch budgets from Supabase
  useEffect(() => {
    if (!user?.id) return;

    const fetchBudgets = async () => {
      setLoading(true);
      try {
        const currentMonth = new Date()?.getMonth() + 1;
        const currentYear = new Date()?.getFullYear();
        const budgetsData = await budgetsService?.getAll(user?.id, currentMonth, currentYear);

        // Transform to categories format
        const categoryMap = {
          groceries: { name: 'Groceries', icon: 'ShoppingCart', color: 'var(--color-success)' },
          dining: { name: 'Food & Dining', icon: 'UtensilsCrossed', color: 'var(--color-success)' },
          transportation: { name: 'Transportation', icon: 'Car', color: 'var(--color-secondary)' },
          entertainment: { name: 'Entertainment', icon: 'Tv', color: 'var(--color-warning)' },
          utilities: { name: 'Utilities', icon: 'Home', color: 'var(--color-primary)' },
          healthcare: { name: 'Healthcare', icon: 'Heart', color: 'var(--color-error)' },
          shopping: { name: 'Shopping', icon: 'ShoppingBag', color: 'var(--color-info)' }
        };

        const transformedCategories = budgetsData?.map(budget => {
          const categoryInfo = categoryMap?.[budget?.category] || { name: budget?.category, icon: 'DollarSign', color: 'var(--color-primary)' };
          const budgetAmount = parseFloat(budget?.budgetAmount || 0);
          const spentAmount = parseFloat(budget?.spentAmount || 0);
          const totalBudget = activeBudget?.totalAmount;
          
          return {
            id: budget?.id,
            name: categoryInfo?.name,
            icon: categoryInfo?.icon,
            color: categoryInfo?.color,
            allocated: budgetAmount,
            spent: spentAmount,
            percentage: totalBudget > 0 ? Math.round((budgetAmount / totalBudget) * 100) : 0
          };
        });

        setCategories(transformedCategories);
      } catch (error) {
        console.error('Error fetching budgets:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchBudgets();

    // Real-time subscription for budget changes
    const budgetsChannel = supabase?.channel('budgets_realtime')?.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'budgets',
          filter: `user_id=eq.${user?.id}`
        },
        () => {
          fetchBudgets();
        }
      )?.subscribe();

    return () => {
      supabase?.removeChannel(budgetsChannel);
    };
  }, [user?.id, activeBudget?.totalAmount]);

  const handleBudgetCreate = async (budgetData) => {
    setActiveBudget(budgetData);
    // Recalculate category allocations based on new total
    const updatedCategories = categories?.map(cat => ({
      ...cat,
      allocated: (budgetData?.totalAmount * cat?.percentage) / 100
    }));
    setCategories(updatedCategories);
  };

  const handleCategoryUpdate = async (updatedCategories) => {
    setCategories(updatedCategories);
    
    // Update budgets in database
    if (user?.id) {
      try {
        const currentMonth = new Date()?.getMonth() + 1;
        const currentYear = new Date()?.getFullYear();
        
        for (const category of updatedCategories) {
          if (category?.id) {
            await budgetsService?.update(category?.id, {
              budgetAmount: category?.allocated,
              month: currentMonth,
              year: currentYear
            });
          }
        }
      } catch (error) {
        console.error('Error updating budgets:', error);
      }
    }
  };

  const totalSpent = categories?.reduce((sum, cat) => sum + cat?.spent, 0);
  const totalAllocated = categories?.reduce((sum, cat) => sum + cat?.allocated, 0);
  const remainingBudget = totalAllocated - totalSpent;

  const breadcrumbs = [
    { label: 'Dashboard', path: '/retail-user-dashboard' },
    { label: 'Budget Management', path: '/budget-management' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation />
      <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-6">
        <NavigationBreadcrumbs items={breadcrumbs} />
        
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6 mt-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">Budget Management</h1>
            <p className="text-sm md:text-base text-muted-foreground">Create, customize, and monitor comprehensive budgets with intelligent spending controls</p>
          </div>
          <Button variant="default" iconName="Download" iconPosition="left" size="lg">
            Export Report
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="bg-card rounded-2xl p-6 shadow-elevation-2">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon name="Wallet" size={20} color="var(--color-primary)" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Budget</p>
                <p className="text-2xl font-bold text-foreground">${totalAllocated?.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-2xl p-6 shadow-elevation-2">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-error/10 flex items-center justify-center">
                <Icon name="TrendingDown" size={20} color="var(--color-error)" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Spent</p>
                <p className="text-2xl font-bold text-foreground">${totalSpent?.toLocaleString()}</p>
              </div>
            </div>
          </div>

          <div className="bg-card rounded-2xl p-6 shadow-elevation-2">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-lg bg-success/10 flex items-center justify-center">
                <Icon name="DollarSign" size={20} color="var(--color-success)" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Remaining</p>
                <p className="text-2xl font-bold text-success">${remainingBudget?.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <BudgetCreationForm 
            activeBudget={activeBudget} 
            onBudgetCreate={handleBudgetCreate} 
          />
          <SafeToSpendCalculator 
            totalAllocated={totalAllocated}
            totalSpent={totalSpent}
            remainingBudget={remainingBudget}
            categories={categories}
          />
        </div>

        <div className="mb-6">
          <CategoryAllocationSliders 
            categories={categories}
            totalBudget={activeBudget?.totalAmount}
            onCategoryUpdate={handleCategoryUpdate}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <OverBudgetAlerts categories={categories} />
          <MonthComparisonChart data={comparisonData} />
        </div>
      </div>
    </div>
  );
};

export default BudgetManagement;